const SLOW_FRAME_MS = 48;
const SCROLL_WINDOW_MS = 260;
const INTERACTION_SETTLE_MS = 360;

function getSectionLabel() {
   if (typeof document === "undefined") return "unknown";

   const element = document.elementFromPoint(
      window.innerWidth / 2,
      Math.min(window.innerHeight - 1, window.innerHeight * 0.52),
   );
   const section = element?.closest?.("section, .section, [class*='site-']");

   if (!section) return "unknown";

   return (
      section.getAttribute("aria-label") ||
      Array.from(section.classList || []).join(".") ||
      section.tagName.toLowerCase()
   );
}

function logPerf(label, data) {
   console.log(
      `%c[Zotaris perf] ${label}`,
      "color:#daff56;background:#101010;padding:2px 6px;border-radius:4px",
      data,
   );
}

export function initPerformanceDebug() {
   if (typeof window === "undefined" || typeof document === "undefined") {
      return () => {};
   }

   const params = new URLSearchParams(window.location.search);
   const requested = params.has("perf") || params.has("debugPerf");
   const stored = window.localStorage?.getItem("zotarisPerfDebug") === "1";
   let enabled = requested || stored;
   let monitoring = false;
   let raf = 0;
   let lastFrame = performance.now();
   let lastScrollAt = 0;
   let lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
   let longTaskObserver = null;

   const onScroll = () => {
      lastScrollAt = performance.now();
      lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
   };

   const frameLoop = (time) => {
      const delta = time - lastFrame;
      lastFrame = time;

      if (enabled && delta > SLOW_FRAME_MS) {
         const duringScroll = time - lastScrollAt < SCROLL_WINDOW_MS;

         logPerf("slow frame", {
            frameMs: Number(delta.toFixed(1)),
            duringScroll,
            scrollY: Math.round(lastScrollY),
            section: getSectionLabel(),
            memoryMB: performance.memory
               ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)
               : "n/a",
         });
      }

      raf = window.requestAnimationFrame(frameLoop);
   };

   const startMonitoring = () => {
      if (monitoring) return;
      monitoring = true;
      lastFrame = performance.now();
      lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;

      window.addEventListener("scroll", onScroll, { passive: true });
      raf = window.requestAnimationFrame(frameLoop);

      if ("PerformanceObserver" in window) {
         try {
            longTaskObserver = new PerformanceObserver((list) => {
               if (!enabled) return;

               list.getEntries().forEach((entry) => {
                  logPerf("long task", {
                     durationMs: Number(entry.duration.toFixed(1)),
                     startMs: Number(entry.startTime.toFixed(1)),
                     scrollY: Math.round(lastScrollY),
                     section: getSectionLabel(),
                     attribution: entry.attribution || [],
                  });
               });
            });
            longTaskObserver.observe({ entryTypes: ["longtask"] });
         } catch {
            longTaskObserver = null;
         }
      }
   };

   const stopMonitoring = () => {
      if (!monitoring) return;
      monitoring = false;
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      longTaskObserver?.disconnect();
      longTaskObserver = null;
   };

   const enable = () => {
      enabled = true;
      window.localStorage?.setItem("zotarisPerfDebug", "1");
      startMonitoring();
      logPerf("enabled", {
         hint: "Scroll, open FAQ items, then watch slow-frame / long-task logs.",
      });
   };

   const disable = () => {
      enabled = false;
      window.localStorage?.removeItem("zotarisPerfDebug");
      stopMonitoring();
      logPerf("disabled", {});
   };

   const markInteraction = (name, detail = {}) => {
      if (!enabled) return;

      const startedAt = performance.now();
      const startedY = window.scrollY || document.documentElement.scrollTop || 0;

      window.requestAnimationFrame(() => {
         const firstFrame = performance.now() - startedAt;

         window.setTimeout(() => {
            logPerf("interaction", {
               name,
               firstFrameMs: Number(firstFrame.toFixed(1)),
               settledMs: Number((performance.now() - startedAt).toFixed(1)),
               scrollY: Math.round(startedY),
               section: getSectionLabel(),
               ...detail,
            });
         }, INTERACTION_SETTLE_MS);
      });
   };

   const report = () => {
      logPerf("snapshot", {
         scrollY: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
         section: getSectionLabel(),
         memoryMB: performance.memory
            ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)
            : "n/a",
      });
   };

   window.zotarisPerf = {
      enable,
      disable,
      markInteraction,
      report,
   };

   if (enabled) {
      startMonitoring();
      logPerf("enabled", {
         hint: "Scroll, open FAQ items, then watch slow-frame / long-task logs.",
      });
   }

   return () => {
      stopMonitoring();
      if (window.zotarisPerf?.markInteraction === markInteraction) {
         delete window.zotarisPerf;
      }
   };
}
